import React, { useRef, useMemo, useState } from 'react';
import { Box, CircularProgress, Divider, IconButton, Menu, MenuItem, Stack, Switch, Typography, useTheme } from '@mui/material';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, ComposedChart, ReferenceLine, Bar, Brush } from 'recharts';
import EmptyState from '@shared/ui/common/EmptyState';
import { Product } from '@domain/product/models/Product';
import { useSalesChart } from '../hooks/useSalesChart';
import { format, getISOWeek, getISOWeekYear, isValid, subWeeks } from 'date-fns';
import { MoreVert } from '@mui/icons-material';

interface SalesChartProps {
  selectedProduct?: Product;
  startDate: Date;
  endDate: Date;
  marketplaces?: string[];
  countries?: string[];
}

const SalesChart: React.FC<SalesChartProps> = ({ selectedProduct, startDate, endDate, marketplaces, countries }) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showHistoricForecast, setShowHistoricForecast] = useState(false);
  const shouldHideForecast = Boolean(marketplaces?.length || countries?.length);

  const { chartData, isLoadingCompanySales } = useSalesChart({ startDate, endDate, showHistoricForecast, marketplaces, countries });

  const currentWeekLabel = useMemo(() => {
    const currentWeek = getISOWeek(new Date());
    const currentYear = getISOWeekYear(new Date());
    return `${currentYear}-W${String(currentWeek).padStart(2, '0')}`;
  }, []);

  const showCurrentWeekLine = useMemo(() => {
    if (!chartData.length) {
      return false;
    }
    return chartData.some((entry) => getISOWeek(entry.date) === getISOWeek(new Date()) && getISOWeekYear(entry.date) === getISOWeekYear(new Date()));
  }, [chartData, currentWeekLabel]);

  return (
    <Box
      flex={1}
      ref={containerRef}
      sx={{
        minHeight: '550px',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'background.paper',
        p: 2,
      }}
    >

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{shouldHideForecast ? 'Weekly Sales Chart' : 'Weekly Sales and Forecast Chart'}</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, backgroundColor: theme.palette.secondary.main, borderRadius: 0.5 }} />
            <Typography variant="body2" color="text.secondary">Sales</Typography>
          </Box>
          {!shouldHideForecast && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, backgroundColor: theme.palette.primary.main, borderRadius: 0.5 }} />
              <Typography variant="body2" color="text.secondary">Forecast</Typography>
            </Box>
          )}

          {!shouldHideForecast && (
            <>
              <IconButton size="small" onClick={(event) => setAnchorEl(event.currentTarget)}><MoreVert /></IconButton>
              <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}  > <MenuItem> Show Historic Forecast <Switch checked={showHistoricForecast} onChange={(event) => setShowHistoricForecast(event.target.checked)} /> </MenuItem> </Menu>
            </>
          )}
        </Box>
      </Box>

    

      {isLoadingCompanySales ? (
        <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : !chartData.length ? (
        <EmptyState
          title="No Sales Data Available"
          description="There is no sales data to display for the selected period."
        />
      ) : (
        <ResponsiveContainer width="100%" height={550}>
          <ComposedChart
            data={chartData}
            margin={{ top: 50, right: 0, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.palette.divider}
              vertical={false}
            />

            <XAxis
              dataKey="date"
              tick={<CustomTick />}
              height={32}
              minTickGap={0}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              orientation="right"
              tick={{ fill: theme.palette.text.secondary, fontSize: 14, fontFamily: theme.typography.fontFamily }}
              tickFormatter={(value) => value.toString()}
            />

            <Tooltip content={(props: any) => <CustomTooltipContent props={props} />} />

            <Bar
              type="monotone"
              dataKey="quantity"
              name="Sales"
              fill={theme.palette.secondary.main}
              isAnimationActive={false}
              radius={[100, 100, 0, 0]}
            />

            {!shouldHideForecast && (
              <Bar
                type="monotone"
                dataKey="forecastQuantity"
                name="Forecast"
                fill={theme.palette.primary.main}
                isAnimationActive={false}
                radius={[100, 100, 0, 0]}
              />
            )}

            {showCurrentWeekLine && (
              <ReferenceLine
                x={currentWeekLabel}
                stroke={theme.palette.secondary.main}
                strokeWidth={1}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};


const CustomTick = ({ x, y, payload }: any) => {
  const theme = useTheme();

  const date = payload.value instanceof Date ? payload.value : new Date(payload.value);

  return (
    <text
      x={x}
      y={y + 10}
      textAnchor="middle"
      fontFamily={theme.typography.fontFamily}
      fontSize={12}
      fill={theme.palette.text.secondary}
    >
      <tspan
        x={x}
        dy={0}
        fontSize={12}
        fill={theme.palette.text.secondary}
      >
        {format(date, "MMM d, yyyy")}
      </tspan>


      <tspan
        x={x}
        dy={14}
        fontSize={10}
        fill={theme.palette.text.secondary}
      >
        Week {getISOWeek(date)}
      </tspan>

    </text>
  );
};



const CustomTooltipContent = ({ props }: { props: { label: string, payload: { name: string, value: number, fill: string }[] } }) => {
  const theme = useTheme();

  const date = new Date(props.label);

  return (

    <Stack direction="column" spacing={1} sx={{ backgroundColor: theme.palette.background.paper, p: 2, borderRadius: 2, boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' }}>

      {isValid(date) && (
        <Stack direction="row" gap={1} justifyContent="space-between" spacing={1}>
          <Typography variant="body2" color="text.secondary">Week {getISOWeek(date)}</Typography>
          <Typography variant="body2" color="text.secondary">{format(date, "MMM d, yyyy")} </Typography>
        </Stack>
      )}

      <Divider />

      {props.payload.map((item) => (
        <Stack direction="row" gap={1} justifyContent="space-between" spacing={1}>

          <Typography variant="body2" color="text.secondary">{item.name}</Typography>

          {item.value !== undefined && item.value !== null && !isNaN(item.value) && (
            <Typography variant="body2" color={item.fill}>
              {item.value.toFixed(0)}
            </Typography>
          )}

        </Stack>
      ))}

    </Stack>

  );
};

export default SalesChart;